import { Info, X, Trash2, Key, Shield } from 'lucide-react';

const NODE_COLORS = {
  source:  { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6' },
  table:   { bg: '#dbeafe', border: '#2563eb', text: '#1d4ed8' },
  domain:  { bg: '#d1fae5', border: '#059669', text: '#065f46' },
};

const Row = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-gray-800 font-medium text-sm">{String(value)}</div>
    </div>
  );
};

const NodeDetails = ({ node, domains, onClose, onRemove }) => {
  if (!node) return null;

  const colors = NODE_COLORS[node.type] || NODE_COLORS.source;

  return (
    <aside className="w-72 bg-white border-l border-gray-200 flex flex-col shrink-0">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <Info className="w-4 h-4 text-gray-400" />
          Node Detail
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 overflow-y-auto flex-1 space-y-4 text-sm">
        <div className="flex items-center gap-2">
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase"
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          >
            {node.type}
          </span>
          <span className="font-semibold text-gray-800 truncate">{node.label}</span>
        </div>

        {node.type === 'source' && (
          <>
            <Row label="Source Type"  value={node.properties?.source_type} />
            <Row label="Connector"    value={node.properties?.connector_type} />
          </>
        )}

        {node.type === 'table' && (
          <>
            <Row label="Full Path"   value={node.id} />
            <Row label="Schema"      value={node.properties?.schema_name} />
            <Row label="Row Count"   value={node.properties?.row_count?.toLocaleString()} />
            {node.properties?.description && (
              <div>
                <div className="text-xs text-gray-400 mb-0.5">Description</div>
                <p className="text-gray-700 text-xs leading-relaxed">{node.properties.description}</p>
              </div>
            )}
            {Array.isArray(node.properties?.columns) && node.properties.columns.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 mb-1">
                  Columns ({node.properties.columns.length})
                </div>
                <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                  {node.properties.columns.map((col) => (
                    <div key={col.name} className="flex items-center gap-1.5 text-xs bg-gray-50 rounded px-2 py-1">
                      {col.primary_key && <Key className="w-3 h-3 text-yellow-600 shrink-0" />}
                      {col.is_pii      && <Shield className="w-3 h-3 text-red-500 shrink-0" />}
                      <span className="font-medium text-gray-800 flex-1 truncate">{col.name}</span>
                      <span className="text-gray-400 shrink-0">{col.data_type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {node.type === 'domain' && (
          <>
            {node.properties?.description && (
              <Row label="Description"   value={node.properties.description} />
            )}
            {node.properties?.parent_domain && (
              <Row label="Parent Domain" value={node.properties.parent_domain} />
            )}
            {(() => {
              const d = domains && domains.find((x) => x.id === node.id);
              return d ? <Row label="Tables Linked" value={d.table_count} /> : null;
            })()}
          </>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 shrink-0">
        <button
          onClick={() => onRemove(node.id)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Remove from view
        </button>
      </div>
    </aside>
  );
};

export default NodeDetails;
